import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { get, del } from "@/lib/api";
import TestDetailModal from "@/components/TestDetailModal";
import { PageLoader, PageHeader, EmptyState, Badge, statusTone } from "@/components/common";
import { ListChecks, Trash2, Eye } from "lucide-react";

export default function MyContent() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");
  const [detailId, setDetailId] = useState(null);
  const { data: courses, isLoading: l1 } = useQuery({ queryKey: ["mc-courses"], queryFn: () => get("/courses/mine") });
  const { data: tests, isLoading: l2 } = useQuery({ queryKey: ["mc-tests"], queryFn: () => get("/tests/mine") });
  const { data: approvals } = useQuery({ queryKey: ["mc-approvals-teacher"], queryFn: () => get("/dashboard/teacher").then((d) => d.approvals) });

  const removeCourse = async (id) => { if (!window.confirm("Delete this course?")) return; await del(`/courses/${id}`); toast.success("Course deleted"); qc.invalidateQueries({ queryKey: ["mc-courses"] }); };
  const removeTest = async (id) => { if (!window.confirm("Delete this test?")) return; await del(`/tests/${id}`); toast.success("Test deleted"); qc.invalidateQueries({ queryKey: ["mc-tests"] }); };

  if (l1 || l2) return <PageLoader />;
  const items = [
    ...(courses || []).map((c) => ({ ...c, ctype: "Course" })),
    ...(tests || []).map((t) => ({ ...t, ctype: "Test" })),
  ];
  const filtered = items.filter((i) => filter === "all" || i.status === filter || (filter === "pending" && i.status === "pending_approval"));

  return (
    <div>
      <PageHeader title="My Content" subtitle="All your courses & tests — click a test to view its questions" />
      <div className="flex flex-wrap gap-2 mb-5">
        {[["all", "All"], ["draft", "Draft"], ["pending", "Pending"], ["published", "Published"], ["rejected", "Rejected"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`content-filter-${k}`} className={`px-4 py-1.5 rounded-full text-sm font-semibold ${filter === k ? "bg-indigo-800 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{l}</button>
        ))}
      </div>
      {filtered.length === 0 ? <EmptyState icon={ListChecks} title="No content in this view" /> : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="text-left p-3">Title</th><th className="text-left p-3">Type</th><th className="text-left p-3">Status</th><th className="text-left p-3">Updated</th><th className="text-right p-3">Actions</th></tr></thead>
            <tbody>{filtered.map((i) => (
              <tr key={i.id} className="border-t border-slate-100" data-testid={`content-row-${i.id}`}>
                <td className="p-3 font-medium text-slate-800">{i.title}</td>
                <td className="p-3"><Badge tone="slate">{i.ctype}</Badge></td>
                <td className="p-3"><Badge tone={statusTone(i.status)}>{i.status}</Badge></td>
                <td className="p-3 text-slate-500">{new Date(i.updated_at || i.created_at).toLocaleDateString()}</td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-3">
                    {i.ctype === "Test" && <button onClick={() => setDetailId(i.id)} data-testid={`view-content-${i.id}`} className="text-indigo-700 flex items-center gap-1 text-xs font-semibold"><Eye className="h-4 w-4" /> View</button>}
                    <button onClick={() => (i.ctype === "Test" ? removeTest(i.id) : removeCourse(i.id))} data-testid={`delete-content-${i.id}`} className="text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {approvals?.some((a) => a.status === "rejected") && (
        <div className="mt-6"><h3 className="font-display font-bold text-lg mb-2">Rejection Reasons</h3>
          {approvals.filter((a) => a.status === "rejected").map((a) => <div key={a.id} className="bg-red-50 border border-red-200 rounded-lg p-3 mb-2 text-sm"><b>{a.title}</b>: {a.reason || "No reason provided"}</div>)}
        </div>
      )}
      {detailId && <TestDetailModal testId={detailId} canManage onClose={() => setDetailId(null)} onChanged={() => qc.invalidateQueries({ queryKey: ["mc-tests"] })} />}
    </div>
  );
}
